import { Injectable } from '@angular/core';
import { DataService } from './data.service';
import { Subject } from 'rxjs';
import { DataChangeEmitPayload, DataChangeEmitWhat, DataChangeEmitWho } from '../models/data-change-emit/data-change-emit-payload';
import { EntityType } from '../entities/EntityType';
import { Callback, CallbackNames } from '../models/db/db-transaction-callback';
import { DbTransaction } from '../models/db/db-transaction';
import { StoreName } from '../entities/StoreName';
import { RecordNotFoundException, RecordSoftDeletedException } from '../utils/exceptions';
import { EqualFilter, InFilter } from '../baseServices/filter.interface';
import { Check, EntityHelper } from '../utils/entity-helper';

@Injectable({
  providedIn: 'root',
})
export class EntityTypeService extends DataService {
  private emitChangeSource = new Subject<DataChangeEmitPayload<EntityType>>();
  changeEmitted$ = this.emitChangeSource.asObservable();

  constructor() {
    super();
  }

  // #region Utility

  emitChange(entityType: EntityType, who: DataChangeEmitWho, what: DataChangeEmitWhat) {
    this.emitChangeSource.next({ data: entityType, who: who, what: what });
  }

  getEmitChangeCallback(entityType: EntityType, who: DataChangeEmitWho, what: DataChangeEmitWhat): Callback {
    return {
      name: CallbackNames.EmitChangeForEntityType + '_' + entityType.RecordId,
      fn: this.emitChange.bind(this, entityType, who, what),
    };
  }

  private async startTransactionIfNeeded(existingTransaction?: DbTransaction): Promise<DbTransaction> {
    return existingTransaction || (await this.repository.startTransaction([StoreName.EntityType], 'readwrite'));
  }

  // #endregion
  // #region Low-level Get One

  async getEntityTypeByOnlineId(onlineId: number, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<EntityType> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const result = await this.repository.getByOnlineId<EntityType>(StoreName.EntityType, onlineId, EntityType, transaction);

    if (result === null) {
      throw new RecordNotFoundException(onlineId);
    }

    const record = result as EntityType;

    if (this.checkIsEntityTypeSoftDeleted(record) && !includeDeleted) {
      throw new RecordSoftDeletedException(onlineId);
    }

    return record;
  }

  async getEntityTypeByRecordId(recordId: number, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<EntityType> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const result = await this.repository.getByLocalId<EntityType>(StoreName.EntityType, recordId, EntityType, transaction);

    if (result === null) {
      throw new RecordNotFoundException(recordId);
    }

    const record = result as EntityType;

    if (this.checkIsEntityTypeSoftDeleted(record) && !includeDeleted) {
      throw new RecordSoftDeletedException(recordId);
    }

    return record;
  }

  // #endregion
  // #region Low-level Get Many

  async getAllEntityTypes(filter: any = undefined, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<EntityType[]> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const result = await this.repository.getAll<EntityType>(StoreName.EntityType, filter, EntityType, transaction);

    let records = result as EntityType[];

    if (!includeDeleted) {
      records = records.filter(entityType => !this.checkIsEntityTypeSoftDeleted(entityType));
    }

    return records;
  }

  async getEntityTypeByOnlineIds(onlineIds: number[], existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<EntityType[]> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const filter: InFilter = { in: { field: 'Id', searchArray: onlineIds } };

    const result = await this.repository.getAll<EntityType>(StoreName.EntityType, filter, EntityType, transaction);

    let records = result as EntityType[];

    if (!includeDeleted) {
      records = result.filter(entityType => !this.checkIsEntityTypeSoftDeleted(entityType));
    }

    return records;
  }

  // #endregion
  // region Low-level Create

  private async createEntityType(who: DataChangeEmitWho, recordToCreate: EntityType, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<number> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // LC1. Validate action
    await this.validateEntityTypeCreation(transaction);

    // TODO: Impelment LC2 and LC3

    // LC4. Repository call
    const recordId = await this.repository.create(recordToCreate, StoreName.EntityType, transaction);
    // LC5. Get created record
    const createdRecord = await this.getEntityTypeByRecordId(recordId, transaction, includeDeleted);
    // LC6. Report changes
    transaction.addOnCommitCallback(this.getEmitChangeCallback(createdRecord, who, DataChangeEmitWhat.Create));

    // LC7. Return id
    return recordId;
  }

  // #endregion
  // region Low-level Update

  async updateEntityType(who: DataChangeEmitWho, recordToUpdate: EntityType, skipLocalCopyValidation: boolean, localCopy: EntityType | null, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // LU1. Get existing record
    const existingRecord = await this.getEntityTypeByRecordId(recordToUpdate.RecordId, transaction);

    // LU2. Validate action
    await this.validateEntityTypeUpdate(recordToUpdate, transaction);

    // LU3. Idempotent check
    if (EntityHelper.checkAllFieldsEqual(existingRecord, recordToUpdate)) {
      return;
    }

    // LU4. Validate local copy
    if (!skipLocalCopyValidation) {
      const checks: Check<EntityType>[] = [{ name: 'ImmutableFields', function: this.checkImmutableFieldsMatch }];
      EntityHelper.validateLocalCopy<EntityType>(localCopy, existingRecord, checks);
    }

    // LU5. Validate model
    await this.validateEntityType(recordToUpdate, transaction);

    // LU6. Repository call
    await this.repository.update(recordToUpdate, StoreName.EntityType, transaction);

    // LU7. Get updated record
    const updatedRecord = await this.getEntityTypeByRecordId(recordToUpdate.RecordId, transaction);

    // LU8. Report changes
    transaction.addOnCommitCallback(this.getEmitChangeCallback(updatedRecord, who, DataChangeEmitWhat.Update));

    // LU9. Return
    return;
  }

  // #endregion
  // region Low-level Delete

  // #endregion
  // region Low-level Validate Model

  async validateEntityType(entityType: EntityType, existingTransaction?: DbTransaction): Promise<void> {
    // TODO: Implement validation logic

    return;
  }

  // #endregion
  // region Low-level Validate Action

  async validateEntityTypeCreation(existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // TODO: Implement validation logic

    return;
  }

  async validateEntityTypeUpdate(entityType: EntityType, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // TODO: Implement validation logic

    return;
  }

  // #endregion
  // region Mid-level Get One

  // #endregion
  // region Mid-level Get Many

  // #endregion
  // region Mid-level Create

  // #endregion
  // region Mid-level Update

  // #endregion
  // region Mid-level Delete

  // #endregion
  // region Mid-level Validate

  // #endregion
  // region Mid-level Upsert

  // #endregion
  // region Mid-level Sync

  async upsertEntityTypeFromSync(who: DataChangeEmitWho, recordToUpsert: EntityType, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    if (who !== DataChangeEmitWho.Sync) {
      throw new Error('Must originate from Sync.');
    }

    try {
      const existingRecord = await this.getEntityTypeByOnlineId(recordToUpsert.Id, transaction, true);
      recordToUpsert.RecordId = existingRecord.RecordId;
      await this.updateEntityType(who, recordToUpsert, true, null, transaction);
    } catch (e) {
      if (e instanceof RecordNotFoundException) {
        await this.createEntityType(who, recordToUpsert, transaction, true);
      }
    }

    return;
  }

  // #endregion
  // region Check

  private checkIsEntityTypeSoftDeleted(record: EntityType): boolean {
    return record.IsDeleted;
  }

  private checkImmutableFieldsMatch(a: EntityType, b: EntityType): boolean {
    return a.Name === b.Name && a.Number === b.Number;
  }

  // #endregion
  // region Lookup

  async getEntityTypeIdByNumber(number: string, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<number> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const filterData: EqualFilter = {
      equals: {
        field: 'Number',
        searchString: number,
        caseInsensitive: true,
      },
    };

    const result = await this.getAllEntityTypes(filterData, transaction, includeDeleted);

    if (result.length > 0) {
      const record = result[0];

      if (this.checkIsEntityTypeSoftDeleted(record) && !includeDeleted) {
        throw new RecordSoftDeletedException(number);
      }

      return record.Id;
    } else {
      throw new RecordNotFoundException(number);
    }
  }

  // #endregion
  // region Transform

  // #endregion
  // region Other

  // #endregion
}
